import { useState } from "react";
import { Plus, Building2, Loader2, Check, ShieldCheck, Send, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ManualCompanyForm } from "@/components/ManualCompanyForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BOARD_ROLES = ["Styreleder", "Nestleder", "Styremedlem", "Varamedlem", "Daglig leder"];

export function AddCompany({ verified, onAdded }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("proff");
  const [results, setResults] = useState(null); // null = laster/ikke lastet
  const [loading, setLoading] = useState(false);
  const [proffError, setProffError] = useState("");
  const [prefillOrg, setPrefillOrg] = useState("");
  const [review, setReview] = useState(null); // {org_number, role, name}
  const [req, setReq] = useState({ name: "", org_number: "", role: "Styremedlem", note: "" });
  const [sending, setSending] = useState(false);

  const loadRoles = async () => {
    setLoading(true);
    setResults(null);
    setProffError("");
    try {
      const { data } = await api.get("/proff/roles");
      setResults(data.companies || []);
      if (data.proff_error) setProffError(data.proff_error);
    } catch (err) {
      setResults([]);
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    if (!verified) {
      toast.info("Verifiser identiteten din først for å legge til selskaper.");
      return;
    }
    setTab("proff");
    setResults(null);
    setProffError("");
    setPrefillOrg("");
    setOpen(true);
    loadRoles();
  };

  // Åpne bekreftelses-popup med Proff-info som kan rettes før selskapet legges til.
  const addCompany = (c) => {
    setReview({ org_number: c.org_number, role: c.role || "", name: c.name });
  };

  const sendRequest = async () => {
    if (!req.name.trim()) {
      toast.error("Skriv inn selskapsnavn");
      return;
    }
    setSending(true);
    try {
      await api.post("/companies/request-access", req);
      toast.success("Forespørsel sendt til admin for godkjenning");
      setReq({ name: "", org_number: "", role: "Styremedlem", note: "" });
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const onTabChange = (v) => {
    if (v === "manual") setPrefillOrg(""); // manuelt tabvalg = tomt skjema
    setTab(v);
  };

  return (
    <>
      <Button size="sm" className="gap-2 active:scale-[0.98]" data-testid="add-company-btn" onClick={openModal}>
        <Plus className="h-4 w-4" /> Legg til selskap
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] !flex flex-col" data-testid="add-company-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle>Legg til selskap</DialogTitle>
            <DialogDescription>
              Selskaper der du har en rolle i Proff vises automatisk. Finner du ikke selskapet, kan du registrere det manuelt eller be om tilgang.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={onTabChange} className="mt-1 flex min-h-0 flex-1 flex-col">
            <TabsList className="w-full shrink-0">
              <TabsTrigger value="proff" data-testid="add-company-tab-proff" className="flex-1">Fra Proff</TabsTrigger>
              <TabsTrigger value="manual" data-testid="add-company-tab-manual" className="flex-1">Registrer manuelt</TabsTrigger>
              <TabsTrigger value="request" data-testid="add-company-tab-request" className="flex-1">Be om tilgang</TabsTrigger>
            </TabsList>

            <TabsContent value="proff" className="mt-4 flex min-h-0 flex-1 flex-col">
              {proffError ? (
                <div className="mb-2 flex shrink-0 items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-300" data-testid="proff-roles-error">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {proffError}
                </div>
              ) : null}

              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1" data-testid="proff-roles-results">
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)
                ) : results === null ? null : results.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="proff-roles-empty">
                    <Building2 className="mx-auto mb-2 h-6 w-6 text-muted-foreground/50" />
                    Vi fant ingen selskaper der du har en rolle i Proff. Registrer selskapet manuelt eller be om tilgang.
                  </div>
                ) : (
                  results.map((c) => (
                    <div
                      key={c.company_id || c.org_number}
                      data-testid={`proff-result-${c.org_number || c.company_id}`}
                      className="flex items-center gap-3 rounded-lg border border-border/60 bg-card p-3"
                    >
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                        <Building2 className="h-5 w-5" strokeWidth={1.7} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{c.name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          Org.nr {c.org_number}
                          {c.role ? ` · ${c.role}` : ""}
                        </p>
                      </div>
                      {c.already_added ? (
                        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary" data-testid={`proff-added-${c.org_number}`}>
                          <Check className="h-4 w-4" /> Lagt til
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          className="shrink-0 gap-1.5"
                          data-testid={`add-proff-${c.org_number}`}
                          onClick={() => addCompany(c)}
                        >
                          <Plus className="h-4 w-4" /> Legg til
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </TabsContent>

            <TabsContent value="manual" className="mt-4 min-h-0 flex-1 overflow-y-auto">
              <ManualCompanyForm key={prefillOrg || "blank"} initialOrg={prefillOrg} onDone={() => { setOpen(false); onAdded?.(); }} />
            </TabsContent>

            <TabsContent value="request" className="mt-4 min-h-0 flex-1 overflow-y-auto">
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Selskapsnavn</Label>
                  <Input data-testid="request-name-input" className="mt-1.5" value={req.name} onChange={(e) => setReq({ ...req, name: e.target.value })} placeholder="Eksempel AS" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Org.nr (valgfritt)</Label>
                    <Input data-testid="request-org-input" className="mt-1.5" value={req.org_number} onChange={(e) => setReq({ ...req, org_number: e.target.value })} placeholder="9 siffer" inputMode="numeric" />
                  </div>
                  <div>
                    <Label className="text-xs">Din rolle</Label>
                    <Select value={req.role} onValueChange={(v) => setReq({ ...req, role: v })}>
                      <SelectTrigger data-testid="request-role-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BOARD_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Melding til admin (valgfritt)</Label>
                  <Input data-testid="request-note-input" className="mt-1.5" value={req.note} onChange={(e) => setReq({ ...req, note: e.target.value })} placeholder="f.eks. hvorfor du trenger tilgang" />
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  En admin verifiserer forespørselen og gir deg tilgang til selskapet.
                </div>
                <Button className="w-full gap-2" data-testid="send-request-btn" onClick={sendRequest} disabled={sending}>
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send forespørsel
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Bekreft/rett Proff-info før selskapet legges til */}
      <Dialog open={!!review} onOpenChange={(o) => { if (!o) setReview(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] !flex flex-col" data-testid="proff-review-dialog">
          <DialogHeader className="shrink-0">
            <DialogTitle>Bekreft selskapsinfo fra Proff</DialogTitle>
            <DialogDescription>
              Vi har hentet informasjonen fra Proff. Sjekk at alt stemmer og rett opp ved behov før du legger til selskapet.
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {review ? (
              <ManualCompanyForm
                key={review.org_number}
                mode="proff-confirm"
                initialOrg={review.org_number}
                proffRole={review.role}
                onDone={() => { setReview(null); setOpen(false); onAdded?.(); }}
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
